// Import styles
import './style.css';

import { auth, db, serverTimestamp } from './firebase-config.js';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    sendEmailVerification
} from 'firebase/auth';
import {
    doc,
    getDoc,
    setDoc
} from 'firebase/firestore';

/* --- Theme Toggle Functionality --- */
function initTheme() {
    const savedTheme = localStorage.getItem('worship-theme') || 'dark';
    document.documentElement.setAttribute('data-theme', savedTheme);
}

function toggleTheme() {
    const currentTheme = document.documentElement.getAttribute('data-theme');
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('worship-theme', newTheme);
}

document.addEventListener('DOMContentLoaded', () => {
    // Initialize theme
    initTheme();

    // --- Form Elements ---
    const loginTab = document.getElementById('login-tab');
    const signupTab = document.getElementById('signup-tab');
    const nameField = document.getElementById('name-field');
    const emailInput = document.getElementById('email');
    const nameInput = document.getElementById('name');
    const passwordInput = document.getElementById('password');
    const submitButton = document.getElementById('submit-button');
    const errorMessage = document.getElementById('error-message');
    const formTitle = document.getElementById('form-title');
    const formSubtitle = document.getElementById('form-subtitle');
    const forgotPasswordLink = document.getElementById('forgot-password-link');

    let isLoginMode = true;

    // --- Tab Switching Logic ---
    loginTab.addEventListener('click', () => {
        if (isLoginMode) return;
        isLoginMode = true;
        loginTab.classList.add('active');
        signupTab.classList.remove('active');
        nameField.style.display = 'none';
        formTitle.textContent = 'Login to Worship Portal';
        formSubtitle.textContent = 'Welcome! Please enter your details to access the Worship Team Portal.';
        submitButton.textContent = 'Log In';
        forgotPasswordLink.style.display = 'block';
        errorMessage.style.display = 'none';
    });

    signupTab.addEventListener('click', () => {
        if (!isLoginMode) return;
        isLoginMode = false;
        signupTab.classList.add('active');
        loginTab.classList.remove('active');
        nameField.style.display = 'block';
        formTitle.textContent = 'Create Worship Portal Account';
        formSubtitle.textContent = 'Sign up to access worship team songs and chord charts!';
        submitButton.textContent = 'Sign Up';
        forgotPasswordLink.style.display = 'none';
        errorMessage.style.display = 'none';
    });

    // --- Forgot Password Logic ---
    forgotPasswordLink.addEventListener('click', (e) => {
        e.preventDefault();
        const email = emailInput.value;
        if (!email) {
            errorMessage.textContent = 'Please enter your email address above to reset your password.';
            errorMessage.style.display = 'block';
            return;
        }

        sendPasswordResetEmail(auth, email)
            .then(() => {
                errorMessage.textContent = 'Password reset email sent! Check your inbox (and spam folder).';
                errorMessage.style.color = '#48bb78'; // Green for success
                errorMessage.style.display = 'block';
            })
            .catch(error => {
                errorMessage.textContent = error.message;
                errorMessage.style.display = 'block';
            });
    });

    // --- Form Submission Logic ---
    submitButton.addEventListener('click', () => {
        const name = nameInput.value;
        const email = emailInput.value;
        const password = passwordInput.value;

        if (isLoginMode) {
            // --- Log In Logic ---
            if (!email || !password) {
                errorMessage.textContent = 'Please enter your email and password.';
                errorMessage.style.display = 'block';
                return;
            }

            console.log('🔐 Login attempt:', {
                email: email,
                passwordLength: password.length,
                authInstance: auth ? 'Present' : 'Missing'
            });

            signInWithEmailAndPassword(auth, email, password)
                .then(async userCredential => {
                    console.log('✅ Login successful:', userCredential.user.email);

                    // Block sign-in until an admin approves the account.
                    // Legacy docs without an `approved` field are treated as approved.
                    try {
                        const worshipUserDoc = await getDoc(doc(db, 'worship_users', userCredential.user.uid));
                        if (worshipUserDoc.exists() && worshipUserDoc.data().approved === false) {
                            await auth.signOut();
                            errorMessage.textContent = 'Your account is pending approval from a church administrator. Please try again later.';
                            errorMessage.style.display = 'block';
                            return;
                        }
                    } catch (approvalError) {
                        // Security rules deny reads for unapproved accounts
                        await auth.signOut();
                        errorMessage.textContent = 'Your account is pending approval from a church administrator. Please try again later.';
                        errorMessage.style.display = 'block';
                        return;
                    }

                    window.location.href = 'index.html'; // Redirect to worship portal on success
                })
                .catch(error => {
                    console.error('❌ Login error:', {
                        code: error.code,
                        message: error.message,
                        stack: error.stack,
                        customData: error.customData,
                        fullError: error
                    });

                    // Add specific handling for API key errors
                    if (error.code === 'auth/invalid-api-key' || error.message.includes('API key')) {
                        console.error('🔑 API KEY ISSUE DETECTED');
                        console.error('This is likely due to API key restrictions in Firebase Console.');
                        console.error('Check: https://console.cloud.google.com/apis/credentials');
                        errorMessage.textContent = 'API key restriction error. Check console for details.';
                    } else {
                        errorMessage.textContent = error.message;
                    }
                    errorMessage.style.display = 'block';
                });
        } else {
            // --- Sign Up Logic ---
            if (!name || !email || !password) {
                errorMessage.textContent = 'Please fill out all fields.';
                errorMessage.style.display = 'block';
                return;
            }

            // First, try to create the Firebase Auth account
            console.log('📝 Signup attempt:', {
                name: name,
                email: email,
                passwordLength: password.length
            });

            createUserWithEmailAndPassword(auth, email, password)
                .then(async userCredential => {
                    console.log('✅ User created successfully:', userCredential.user.email);

                    // --- SEND VERIFICATION EMAIL ---
                    await sendEmailVerification(userCredential.user);
                    console.log('📧 Verification email sent');

                    // Create a user document in worship_users collection for the worship portal.
                    // Security rules require self-created accounts to start as an
                    // unapproved student; an admin approves and assigns roles afterward.
                    const worshipUserDocRef = doc(db, 'worship_users', userCredential.user.uid);
                    await setDoc(worshipUserDocRef, {
                        name: name,
                        email: userCredential.user.email,
                        role: 'student',
                        approved: false,
                        createdAt: serverTimestamp()
                    });

                    // Notify admins there is a pending account (fire-and-forget)
                    fetch('/api/approval-notification', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ app: 'worship', name: name, email: userCredential.user.email })
                    }).catch(() => {});

                    // Stay signed out until approved
                    await auth.signOut();

                    // --- INFORM THE USER ---
                    errorMessage.textContent = 'Account created! Please verify your email. A church administrator will approve your account before you can log in.';
                    errorMessage.style.color = '#48bb78'; // Green for success
                    errorMessage.style.display = 'block';
                    // Disable form fields to prevent re-submission
                    submitButton.disabled = true;
                    nameInput.disabled = true;
                    emailInput.disabled = true;
                    passwordInput.disabled = true;
                })
                .catch(async error => {
                    console.error('❌ Signup error:', {
                        code: error.code,
                        message: error.message,
                        fullError: error
                    });

                    // Handle the case where email already exists
                    if (error.code === 'auth/email-already-in-use') {
                        console.log('⚠️ Email already in use, attempting to sign in...');
                        // Try to sign in with the existing account instead
                        try {
                            const userCredential = await signInWithEmailAndPassword(auth, email, password);

                            // Check if this user already has a worship_users document.
                            // A permission error here means the doc exists but is unapproved
                            // (security rules deny reads for unapproved accounts).
                            const worshipUserDocRef = doc(db, 'worship_users', userCredential.user.uid);
                            let worshipUserDoc;
                            try {
                                worshipUserDoc = await getDoc(worshipUserDocRef);
                            } catch (approvalError) {
                                await auth.signOut();
                                errorMessage.textContent = 'Your account is pending approval from a church administrator. Please try again later.';
                                errorMessage.style.display = 'block';
                                return;
                            }

                            if (!worshipUserDoc.exists()) {
                                // User exists but not in worship app - create worship profile.
                                // Try to reuse their name from the music or kids app.
                                let userName = name; // Use the name they entered

                                try {
                                    const musicUserDocRef = doc(db, 'music_users', userCredential.user.uid);
                                    const musicUserDoc = await getDoc(musicUserDocRef);
                                    if (musicUserDoc.exists()) {
                                        userName = musicUserDoc.data().name || userName;
                                    } else {
                                        // Check kids app as fallback
                                        const kidsUserDocRef = doc(db, 'users', userCredential.user.uid);
                                        const kidsUserDoc = await getDoc(kidsUserDocRef);
                                        if (kidsUserDoc.exists()) {
                                            userName = kidsUserDoc.data().name || userName;
                                        }
                                    }
                                } catch (error) {
                                    console.log('Error checking existing profiles:', error);
                                }

                                // Security rules require self-created accounts to start
                                // as an unapproved student
                                await setDoc(worshipUserDocRef, {
                                    name: userName,
                                    email: userCredential.user.email,
                                    role: 'student',
                                    approved: false,
                                    createdAt: serverTimestamp()
                                });

                                // Notify admins there is a pending account (fire-and-forget)
                                fetch('/api/approval-notification', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ app: 'worship', name: userName, email: userCredential.user.email })
                                }).catch(() => {});

                                await auth.signOut();
                                errorMessage.textContent = 'Worship access requested! A church administrator will approve your account before you can log in.';
                                errorMessage.style.display = 'block';
                            } else if (worshipUserDoc.data().approved === false) {
                                await auth.signOut();
                                errorMessage.textContent = 'Your account is pending approval from a church administrator. Please try again later.';
                                errorMessage.style.display = 'block';
                            } else {
                                // User already has approved worship access, just redirect
                                window.location.href = 'index.html';
                            }
                        } catch (signInError) {
                            errorMessage.textContent = 'This email is already registered. Please use the Login tab with your existing password.';
                            errorMessage.style.display = 'block';
                        }
                    } else {
                        errorMessage.textContent = error.message;
                        errorMessage.style.display = 'block';
                    }
                });
        }
    });
});
